import { useState, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Link2, Copy, Smartphone, Monitor, DownloadCloud, PlusCircle, CheckCircle2, ExternalLink } from 'lucide-react';
import { useSubscription } from '../hooks/useApi';
import { AppLang } from '../i18n';
import './SetupPage.css';

interface SetupPageProps {
  user: any;
  lang: AppLang;
  subPage: string | null;
  onNavigate: (page: string) => void;
  onBack: () => void;
  onTabChange: (tab: any) => void;
}

const detectPlatform = () => {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'Android';
  if (/iphone|ipad/i.test(ua)) return 'iOS';
  if (/windows/i.test(ua)) return 'Windows';
  if (/mac/i.test(ua)) return 'macOS';
  return 'Windows';
};

const PLATFORMS = [
  { id: 'ios', label: 'iOS', icon: <Smartphone size={28} /> },
  { id: 'android', label: 'Android', icon: <Smartphone size={28} /> },
  { id: 'macos', label: 'macOS', icon: <Monitor size={28} /> },
  { id: 'windows', label: 'Windows', icon: <Monitor size={28} /> },
];

const APP_LINKS: Record<string, string> = {
  iOS: 'https://apps.apple.com/us/app/happ-proxy-utility/id6504287215',
  Android: 'https://play.google.com/store/apps/details?id=com.happproxy',
  Windows: 'https://happ.info/',
  macOS: 'https://happ.info/',
};

// iOS: основное приложение Happ (стор США) и INCY из РФ-стора для тех, кто не меняет регион
const IOS_STORE_US = 'https://apps.apple.com/us/app/happ-proxy-utility/id6504287215';
const IOS_STORE_INCY = 'https://apps.apple.com/ru/app/incy/id6756943388';

const PLATFORM_INSTRUCTIONS: Record<string, { steps: { title: string; desc: string }[] }> = {
  iOS: {
    steps: [
      { title: 'Выберите приложение', desc: 'Happ — основное: подписка добавляется автоматически, но приложение лежит в американском App Store. INCY — если менять регион не хотите: есть в российском сторе, но ссылку подписки в него нужно вставить вручную. Обе кнопки ниже.' },
      { title: 'Не видно Happ? Смените регион', desc: 'Нужно только для Happ: Настройки → нажмите ваше имя вверху → «Медиаматериалы и покупки» → «Просмотреть» → «Страна/регион» → «Изменить» → выберите США. Карта не нужна, при оплате выберите «Нет». После установки регион можно вернуть обратно. Не хотите менять — ставьте INCY.' },
      { title: 'Добавьте подписку', desc: 'Нажмите кнопку ниже — подписка автоматически добавится в Happ' },
      { title: 'Включите VPN', desc: 'Откройте Happ и нажмите круглую кнопку включения' },
    ],
  },
  Android: {
    steps: [
      { title: 'Скачайте Happ', desc: 'Установите приложение Happ из Google Play на ваше устройство' },
      { title: 'Добавьте подписку', desc: 'Нажмите кнопку ниже — подписка автоматически добавится в Happ' },
      { title: 'Включите VPN', desc: 'Откройте Happ и нажмите круглую кнопку включения' },
    ],
  },
  Windows: {
    steps: [
      { title: 'Скачайте Happ', desc: 'Загрузите и установите Happ для Windows (.exe)' },
      { title: 'Скопируйте ссылку', desc: 'Нажмите кнопку копирования ниже, затем вставьте ссылку в поле импорта в Happ' },
      { title: 'Включите VPN', desc: 'Выберите сервер и нажмите кнопку подключения в Happ' },
    ],
  },
  macOS: {
    steps: [
      { title: 'Скачайте Happ', desc: 'Загрузите и установите Happ для macOS (.dmg)' },
      { title: 'Скопируйте ссылку', desc: 'Нажмите кнопку копирования ниже, затем вставьте ссылку в поле импорта в Happ' },
      { title: 'Включите VPN', desc: 'Выберите сервер и нажмите кнопку подключения в Happ' },
    ],
  },
};

/* ── Reusable animated orb ── */
const AnimatedOrb = ({ icon, step }: { icon: React.ReactNode; step: number }) => (
  <div className="sp-orb-container">
    <div className={`sp-radar sp-radar--step${step}`} />
    <div className="sp-orbit sp-orbit--1">
      <div className="sp-orbit-dot" style={{ top: '-4px', left: '50%' }} />
      <div className="sp-orbit-dot" style={{ bottom: '-4px', left: '50%' }} />
    </div>
    <div className="sp-orbit sp-orbit--2">
      <div className="sp-orbit-dot" style={{ top: '15%', right: '-4px' }} />
      <div className="sp-orbit-dot" style={{ bottom: '15%', left: '-4px' }} />
      <div className="sp-orbit-dot" style={{ top: '-4px', left: '40%' }} />
    </div>
    <div className="sp-orbit sp-orbit--3">
      <div className="sp-orbit-dot" style={{ top: '50%', right: '-4px' }} />
      <div className="sp-orbit-dot" style={{ bottom: '-4px', left: '30%' }} />
    </div>
    <div className="sp-particle sp-particle--1" />
    <div className="sp-particle sp-particle--2" />
    <div className="sp-particle sp-particle--3" />
    <div className="sp-particle sp-particle--4" />
    <div className="sp-particle sp-particle--5" />
    <div className="sp-particle sp-particle--6" />
    <div className="sp-pulse-ring" />
    <div className="sp-pulse-ring sp-pulse-ring--2" />
    <div className="sp-pulse-ring sp-pulse-ring--3" />
    <div className="sp-pulse-ring sp-pulse-ring--4" />
    <div className="sp-pulse-ring sp-pulse-ring--5" />
    <motion.div
      className="sp-center-glass"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 180, damping: 14, delay: 0.1 }}
    >
      <div className="sp-center-border" />
      <motion.div
        className={`sp-center-icon sp-center-icon--step${step}`}
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3, type: 'spring', stiffness: 150 }}
      >
        {icon}
      </motion.div>
    </motion.div>
  </div>
);

export const SetupPage = ({ lang: _lang, subPage, onNavigate, onBack, onTabChange }: SetupPageProps) => {
  void _lang;
  const { data: subData } = useSubscription();
  const detectedPlatform = useMemo(() => detectPlatform(), []);
  const subUrl = subData?.subscription?.sub_url || '';
  // Зашифрованная happ://crypt5 ссылка (конфиг скрыт). Фолбэк — happ://add.
  const happLink = subData?.subscription?.happ_link || (subUrl ? `happ://add/${subUrl}` : '');
  const [step, setStep] = useState(0);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedPlain, setCopiedPlain] = useState(false);
  const platform = detectedPlatform;

  const handleCopyLink = useCallback(() => {
    if (happLink) {
      navigator.clipboard.writeText(happLink);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  }, [happLink]);

  // Обычная https-ссылка — работает во ВСЕХ клиентах (Happ Plus, v2rayNG,
  // Streisand, Hiddify и т.д.), в отличие от happ://crypt5.
  const handleCopyPlain = useCallback(() => {
    if (subUrl) {
      navigator.clipboard.writeText(subUrl).catch(() => {});
      setCopiedPlain(true);
      setTimeout(() => setCopiedPlain(false), 2000);
    }
  }, [subUrl]);

  const openExternal = useCallback((url: string) => {
    const tg = (window as any).Telegram?.WebApp;
    // For app store links, use openLink with try_instant_view: false
    // to prevent in-app browser from intercepting store/download links
    if (tg?.openLink) tg.openLink(url, { try_instant_view: false });
    else window.open(url, '_blank');
  }, []);

  const handleInstallApp = useCallback((p?: string) => {
    const target = p || platform;
    openExternal(APP_LINKS[target] || APP_LINKS['Windows']);
  }, [platform, openExternal]);

  const handleAddSubscription = useCallback(() => {
    if (!happLink) return;
    // happLink уже зашифрован (happ://crypt5) — копируем и открываем именно его.
    navigator.clipboard.writeText(happLink).catch(() => {});
    // Telegram WebView blocks custom protocols (happ://) directly.
    // Use the /go HTTPS redirect page that does JS redirect to happ://
    const goUrl = `/go?url=${encodeURIComponent(happLink)}`;
    const tg = (window as any).Telegram?.WebApp;
    if (tg?.openLink) tg.openLink(window.location.origin + goUrl, { try_instant_view: false });
    else window.open(goUrl, '_blank');
  }, [happLink]);

  const handleFinish = useCallback(() => {
    onTabChange('home');
  }, [onTabChange]);

  // Sub-page: instruction for specific platform
  if (subPage?.startsWith('instruction-')) {
    const instrPlatform = subPage.replace('instruction-', '');
    const platformLabel = PLATFORMS.find(p => p.id === instrPlatform)?.label || instrPlatform;
    const instructions = PLATFORM_INSTRUCTIONS[platformLabel] || PLATFORM_INSTRUCTIONS['Windows'];
    const isMobile = platformLabel === 'iOS' || platformLabel === 'Android';

    return (
      <div className="setup">
        <div className="setup-header">
          <button className="setup-back" onClick={() => onNavigate('other-device')}><ArrowLeft size={20} /></button>
          <span className="setup-header-title">Инструкция для {platformLabel}</span>
        </div>
        <motion.div className="setup-content" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="setup-instr-steps">
            {instructions.steps.map((s, i) => (
              <div key={i} className="setup-instr-step">
                <div className="setup-instr-num">{i + 1}</div>
                <div className="setup-instr-info">
                  <span className="setup-instr-title">{s.title}</span>
                  <span className="setup-instr-desc">{s.desc}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="setup-instr-actions">
            {platformLabel === 'iOS' ? (
              <>
                <button className="setup-btn setup-btn--primary" onClick={() => openExternal(IOS_STORE_US)}>
                  <span className="setup-btn-icon"><DownloadCloud size={20} /></span>
                  🍎 Happ — App Store США
                  <ExternalLink size={16} style={{ marginLeft: 'auto', opacity: 0.6 }} />
                </button>
                <button className="setup-btn setup-btn--secondary" onClick={() => openExternal(IOS_STORE_INCY)}>
                  <span className="setup-btn-icon"><DownloadCloud size={20} /></span>
                  🇷🇺 INCY — без смены региона
                  <ExternalLink size={16} style={{ marginLeft: 'auto', opacity: 0.6 }} />
                </button>
              </>
            ) : (
              <button className="setup-btn setup-btn--primary" onClick={() => handleInstallApp(platformLabel)}>
                <span className="setup-btn-icon"><DownloadCloud size={20} /></span>
                Скачать Happ для {platformLabel}
                <ExternalLink size={16} style={{ marginLeft: 'auto', opacity: 0.6 }} />
              </button>
            )}

            {isMobile ? (
              <button className="setup-btn setup-btn--secondary" onClick={handleAddSubscription}>
                <span className="setup-btn-icon"><PlusCircle size={20} /></span>
                Добавить подписку в Happ
              </button>
            ) : (
              <div className="setup-link-box">
                <div className="setup-link-text">
                  <span className="setup-link-url">{happLink.length > 28 ? happLink.slice(0, 28) + '...' : happLink}</span>
                  <span className="setup-link-label">{copiedLink ? 'Скопировано!' : 'Ваша ссылка на подписку (Happ)'}</span>
                </div>
                <button className="setup-link-copy" onClick={handleCopyLink}>
                  <Copy size={18} />
                </button>
              </div>
            )}

                <div className="setup-plain-link">
                  <div className="setup-plain-title">Другое приложение?</div>
                  <div className="setup-link-box">
                    <div className="setup-link-text">
                      <span className="setup-link-url">{subUrl.length > 28 ? subUrl.slice(0, 28) + '...' : subUrl}</span>
                      <span className="setup-link-label">{copiedPlain ? 'Скопировано!' : 'Универсальная ссылка — для любого клиента'}</span>
                    </div>
                    <button className="setup-link-copy" onClick={handleCopyPlain}>
                      <Copy size={18} />
                    </button>
                  </div>
                </div>

          </div>
        </motion.div>
      </div>
    );
  }

  // Sub-page: other device setup
  if (subPage === 'other-device') {
    return (
      <div className="setup">
        <div className="setup-header">
          <button className="setup-back" onClick={onBack}><ArrowLeft size={20} /></button>
        </div>
        <motion.div className="setup-content" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="setup-hero-card">
            <div className="setup-hero-icon"><Link2 size={28} /></div>
            <h2 className="setup-hero-title">Установка на другом устройстве</h2>
            <p className="setup-hero-sub">Выберите платформу для подключения</p>
          </div>

          <div className="setup-platforms-grid">
            {PLATFORMS.map(p => (
              <button key={p.id} className="setup-platform-card" onClick={() => {
                onNavigate(`instruction-${p.id}`);
              }}>
                <span className="setup-platform-icon">{p.icon}</span>
                <span className="setup-platform-sub">Инструкция</span>
                <span className="setup-platform-name">для {p.label}</span>
              </button>
            ))}
          </div>

          {subUrl && (
            <div className="setup-other-link">
              <span className="setup-other-link-title">Другие платформы</span>
              <div className="setup-link-box">
                <div className="setup-link-text">
                  <span className="setup-link-url">{happLink.length > 28 ? happLink.slice(0, 28) + '...' : happLink}</span>
                  <span className="setup-link-label">{copiedLink ? 'Скопировано!' : 'Ваша ссылка на подписку (Happ)'}</span>
                </div>
                <button className="setup-link-copy" onClick={handleCopyLink}>
                  <Copy size={18} />
                </button>
              </div>

                <div className="setup-plain-link">
                  <div className="setup-plain-title">Другое приложение?</div>
                  <div className="setup-link-box">
                    <div className="setup-link-text">
                      <span className="setup-link-url">{subUrl.length > 28 ? subUrl.slice(0, 28) + '...' : subUrl}</span>
                      <span className="setup-link-label">{copiedPlain ? 'Скопировано!' : 'Универсальная ссылка — для любого клиента'}</span>
                    </div>
                    <button className="setup-link-copy" onClick={handleCopyPlain}>
                      <Copy size={18} />
                    </button>
                  </div>
                </div>
            </div>
          )}
        </motion.div>
      </div>
    );
  }

  // Step wizard data
  const STEPS = [
    {
      icon: <DownloadCloud size={46} strokeWidth={1.5} />,
      title: 'Приложение',
      subtitle: `Установите приложение Happ\nи вернитесь к этому экрану`,
      primaryLabel: 'Установить приложение',
      primaryIcon: <DownloadCloud size={20} />,
      primaryAction: () => handleInstallApp(),
      hasNext: true,
    },
    {
      icon: <PlusCircle size={46} strokeWidth={1.5} />,
      title: 'Подписка',
      subtitle: `Добавьте подписку в приложение\nHapp с помощью кнопки ниже`,
      primaryLabel: 'Добавить подписку',
      primaryIcon: <PlusCircle size={20} />,
      primaryAction: handleAddSubscription,
      hasNext: true,
    },
    {
      icon: <CheckCircle2 size={46} strokeWidth={1.5} />,
      title: 'Готово!',
      subtitle: `VPN настроен и готов к работе.\nОткройте Happ и включите защиту`,
      primaryLabel: 'На главную',
      primaryIcon: <CheckCircle2 size={20} />,
      primaryAction: handleFinish,
      hasNext: false,
    },
  ];

  const currentStep = STEPS[step];

  return (
    <div className="setup">
      {/* Back to previous step */}
      {step > 0 && (
        <div className="setup-header">
          <button className="setup-back" onClick={() => setStep(s => s - 1)}><ArrowLeft size={20} /></button>
        </div>
      )}

      {/* Step indicators */}
      <div className="setup-step-bar">
        {STEPS.map((_, i) => (
          <div key={i} className={`setup-step-dot ${i === step ? 'setup-step-dot--active' : ''} ${i < step ? 'setup-step-dot--done' : ''}`} />
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          className="setup-main-hero"
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.3 }}
        >
          <AnimatedOrb icon={currentStep.icon} step={step} />
          <h1 className="setup-main-title">{currentStep.title}</h1>
          <p className="setup-main-sub">{currentStep.subtitle}</p>
        </motion.div>
      </AnimatePresence>

      <motion.div className="setup-actions" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
        <button className="setup-btn setup-btn--primary" onClick={currentStep.primaryAction}>
          <span className="setup-btn-icon">{currentStep.primaryIcon}</span>
          {currentStep.primaryLabel}
        </button>
        {currentStep.hasNext && (
          <button className="setup-btn setup-btn--secondary" onClick={() => setStep(s => Math.min(s + 1, 2))}>
            Следующий шаг
            <ArrowRight size={18} style={{ marginLeft: 8 }} />
          </button>
        )}
        {step === 0 && (
          <button className="setup-btn setup-btn--ghost" onClick={() => onNavigate('other-device')}>
            Установить на другом устройстве
          </button>
        )}
      </motion.div>
    </div>
  );
};
